import {Thumbmark} from '@thumbmarkjs/thumbmarkjs';
import type {CSSObject} from './types/css';
import type {GetPaymentMethodsResponse} from './types';

export const transformStyle = (style: CSSObject) => {
  return Object.keys(style).reduce<any>((result, prop) => {
    if (typeof style[prop] === 'string') {
      result[prop] = style[prop];
    } else {
      result[prop] = `${style[prop]}px`;
    }
    return result;
  }, {});
};

export const loadScript = (
  src: string,
  nonce?: string,
  dataset?: Record<string, string | undefined>,
  forceReload?: boolean
): Promise<HTMLScriptElement> => {
  return new Promise((resolve, reject) => {
    const existingScript = Array.from(document.scripts).find((s) => s.src === src);
    if (existingScript) {
      if (forceReload) {
        existingScript.remove();
      } else {
        resolve(existingScript);
        return;
      }
    }
    const script = document.createElement('script');
    script.src = src;
    script.onload = () => resolve(script);
    script.onerror = (error) => reject(error);
    if (nonce) {
      script.setAttribute('nonce', nonce);
    }
    if (dataset) {
      Object.keys(dataset).forEach((key) => {
        if (dataset[key]) {
          script.dataset[key] = dataset[key];
        }
      });
    }
    document.head.appendChild(script);
  });
};

export const stringify = (obj: Record<string, any>) => {
  return Object.keys(obj)
    .reduce<string[]>((result, key) => {
      if (obj[key] !== undefined) {
        result.push(`${encodeURIComponent(key)}=${encodeURIComponent(obj[key])}`);
      }
      return result;
    }, [])
    .join('&');
};

export const fixLocalstorage = () => {
  let localStorage: Storage;
  try {
    ({localStorage} = window);
  } catch (_error) {
    const localStorageMock = (() => {
      let store = new Map();
      return {
        getItem(key: string): string {
          return store.get(key);
        },

        setItem: function (key: string, value: string) {
          store.set(key, value);
        },

        clear: function () {
          store = new Map();
        },

        removeItem: function (key: string) {
          store.delete(key);
        }
      };
    })();

    Object.defineProperty(window, 'localStorage', {value: localStorageMock});
    localStorage = localStorageMock as Storage;
  }
  return localStorage;
};

const getLanguage = () => {
  let lang;
  const localStorage = fixLocalstorage();
  try {
    lang = localStorage.getItem('i18nextLng');
  } catch (_error) {}
  if (lang) {
    return lang.split('-')[0];
  }
  return '';
};

// parsePx and toCssPx — use from @monei-js/shared/lib/utils
export {parsePx, toCssPx} from '@monei-js/shared/lib/utils';

const safe = (fn: () => unknown): string => {
  try {
    return String(fn() ?? '');
  } catch {
    return '';
  }
};

export const getClientEnv = () => {
  const env = [
    window.screen.colorDepth,
    window.screen.height,
    window.screen.width,
    new Date().getTimezoneOffset(),
    getLanguage(),
    safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    safe(() => window.devicePixelRatio),
    safe(() => navigator.hardwareConcurrency),
    safe(() => (navigator as any).deviceMemory),
    safe(() => navigator.maxTouchPoints),
    safe(() => navigator.platform),
    safe(() => navigator.languages?.join(','))
  ].join('|');
  return window.btoa(env);
};

const FINGERPRINT_SEED_KEY = '_monei_fp';

const randomHex = (length: number): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(length);
    crypto.getRandomValues(bytes);
    return Array.from(bytes, (b) => (b % 16).toString(16)).join('');
  }
  return Array.from({length}, () => Math.floor(Math.random() * 16).toString(16)).join('');
};

let cachedSeed: string | null = null;

const getPersistentSeed = async (): Promise<Record<string, string>> => {
  if (cachedSeed) return {seed: cachedSeed};
  try {
    const localStorage = fixLocalstorage();
    cachedSeed = localStorage.getItem(FINGERPRINT_SEED_KEY);
    if (!cachedSeed) {
      cachedSeed = randomHex(32);
      try {
        localStorage.setItem(FINGERPRINT_SEED_KEY, cachedSeed);
      } catch {}
    }
  } catch {
    cachedSeed = randomHex(32);
  }
  return {seed: cachedSeed};
};

const thumbmark = new Thumbmark({logging: false});
thumbmark.includeComponent('persistent_seed', getPersistentSeed);

export const getClientFingerprint = async (): Promise<string> => {
  const [, result] = await withError(thumbmark.get());
  return result?.thumbmark ?? '';
};

export const withError = async <T>(p: Promise<T>): Promise<[Error | undefined, T | undefined]> => {
  try {
    return [undefined, await p];
  } catch (err) {
    return [err as Error, undefined];
  }
};

export const isMethodSupported = (config: GetPaymentMethodsResponse, paymentMethod: string) => {
  return config.paymentMethods && config.paymentMethods.includes(paymentMethod);
};

export const validateComponentProps = ({props}: {props: any}) => {
  if (props.paymentId) return;
  if (props.accountId && props.amount && props.currency) return;
  throw new Error('You need to provide paymentId or accountId amount and currency');
};
